import React, { useRef, useState } from "react";
import { galleryImages, OFFER_ANCHOR, CTA_TEXT } from "../../data/mock";
import { Button } from "../ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../ui/carousel";
import { Play, Volume2, VolumeX, Check } from "lucide-react";

const packContents = [
  "Mais de 1 mil artes prontas e editáveis",
  "Tags de agradecimento e datas comemorativas",
  "Sacolas personalizadas de todos os tamanhos",
  "Caixas exclusivas para presentes e kits",
  "Adesivos, lacres e etiquetas premium",
  "Kits temáticos completos por data comemorativa",
  "Kits escolares e infantis personalizados",
  "Cartões de agradecimento modernos",
  "Embalagens sofisticadas para valorizar o produto",
  "Coleções exclusivas atualizadas periodicamente",
  "Kits decorativos para eventos e datas especiais",
  "Elementos gráficos para personalizar cada arte",
];

const Gallery = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setIsPlaying(true); }
    else { v.pause(); setIsPlaying(false); }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section className="section-pad bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-serif-display font-black text-2xl md:text-5xl text-pink-700 leading-tight">
            Veja o que você consegue criar com o Acervo da Artesã
          </h2>
          <p className="mt-3 text-pink-900/70 text-sm md:text-lg">
            Mais de <strong>1 mil artes prontas</strong> — categorizadas, editáveis no Canva e prontas para imprimir.
          </p>
        </div>

        {/* Showcase image + Video side by side */}
        <div className="mt-6 md:mt-10 grid md:grid-cols-2 gap-5 md:gap-6 items-stretch">
          <figure className="card-hover relative overflow-hidden rounded-2xl bg-pink-50 border border-pink-100 shadow-sm">
            <img
              src="/site-images/showcase.png"
              alt="Antes e depois — Acervo da Artesã"
              className="w-full h-auto block"
              loading="lazy"
            />
          </figure>

          <div
            className="card-hover relative overflow-hidden rounded-2xl bg-pink-100 border border-pink-100 shadow-sm cursor-pointer group aspect-[9/16] md:aspect-auto md:min-h-full"
            onClick={togglePlay}
          >
            <video
              ref={videoRef}
              src="/site-images/acervo.mp4"
              poster="/site-images/poster.jpg"
              className="absolute inset-0 w-full h-full object-cover"
              playsInline
              muted={muted}
              loop
              preload="metadata"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center bg-pink-900/25 group-hover:bg-pink-900/35 transition">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 shadow-2xl flex items-center justify-center group-hover:scale-110 transition">
                  <Play className="w-7 h-7 md:w-9 md:h-9 text-pink-600 fill-pink-600 ml-1" />
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? "Ativar som" : "Desativar som"}
              className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-pink-700 flex items-center justify-center shadow-md"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Carousel of images + What's in the pack list */}
        <div className="mt-8 md:mt-14 grid lg:grid-cols-5 gap-6 md:gap-10 items-center">
          {/* Carousel */}
          <div className="lg:col-span-3">
            <Carousel
              opts={{ align: "start", loop: true }}
              className="w-full"
            >
              <CarouselContent className="-ml-3">
                {galleryImages.map((img, i) => (
                  <CarouselItem key={i} className="pl-3 basis-1/2 sm:basis-1/3">
                    <div className="card-hover group relative overflow-hidden rounded-2xl bg-pink-50 border border-pink-100 shadow-sm">
                      <div className="aspect-[3/4] overflow-hidden">
                        <img
                          src={img.src}
                          alt={img.label}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex -left-4 bg-white border-pink-200 text-pink-600 hover:bg-pink-50" />
              <CarouselNext className="hidden md:flex -right-4 bg-white border-pink-200 text-pink-600 hover:bg-pink-50" />
            </Carousel>
            <p className="mt-3 text-center text-xs text-pink-900/60 md:hidden">← Arraste para o lado para ver mais →</p>
          </div>

          {/* What's in the pack list */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 border-2 border-pink-200 rounded-2xl p-5 md:p-6 shadow-sm">
              <h3 className="font-serif-display font-black text-lg md:text-2xl text-pink-700 leading-tight">
                O que está dentro do pack:
              </h3>
              <ul className="mt-3 md:mt-4 space-y-2">
                {packContents.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-pink-900 text-[13px] md:text-sm">
                    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-pink-500 text-white grid place-content-center shadow-sm">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <p className="mt-6 md:mt-8 text-center text-pink-900/70 max-w-2xl mx-auto text-sm md:text-base">
          Suas clientes vão perceber <strong>mais valor</strong> no seu trabalho já na primeira entrega.
        </p>

        <div className="mt-6 md:mt-8 text-center">
          <a href={OFFER_ANCHOR}>
            <Button size="lg" className="w-full sm:w-auto bg-gradient-to-b from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-bold px-6 md:px-8 py-5 rounded-full shadow-lg text-sm md:text-base">
              👉 {CTA_TEXT}
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
